import type { CellContext } from '@tanstack/react-table';
import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';

import type { W_JobMaterialCostHistory_FindForJobMaterial_VM_Single } from 'ab-api/web/jobMaterialCost/findForJobMaterial';

import * as TimeUtils from '@acceligentllc/shared/utils/time';

import { TimeFormat } from '@acceligentllc/shared/enums/timeFormat';

import type { RootState } from 'af-reducers';

import { dollarFormatterUnitPrice } from 'af-utils/format.util';

import MaterialCostSource from 'ab-enums/materialCostSource.enum';

import { PLATFORM_EARLIEST_DATE } from 'ab-common/constants/value';

import { Button } from '@acceligentllc/storybook';

import SimpleTable from 'af-components/Controls/SimpleTable';
import type { SimpleTableProps } from 'af-components/Controls/SimpleTable/types';
import CustomModal from 'af-components/CustomModal';
import MaterialRateSource from 'af-components/MaterialRateSource';

import * as JobMaterialCostHistoryActions from 'af-actions/jobMaterialCost';

interface OwnProps {
	material: Nullable<{ jobMaterialId: number; displayName: string; }>;
	close: () => void;
}

type Props = OwnProps & ConnectedProps<typeof connector>;

const RateHistoryModal: React.FC<Props> = (props) => {
	const { material, timezone, findForJobMaterial, close } = props;

	const showModal = !!material;

	const [costHistories, setCostHistories] = React.useState<Nullable<W_JobMaterialCostHistory_FindForJobMaterial_VM_Single[]>>(null);

	const loadCostHistories = React.useCallback(async () => {
		if (!material) {
			return;
		}
		const loadedCostHistories = await findForJobMaterial(material.jobMaterialId);
		setCostHistories(loadedCostHistories.costHistories);
	}, [findForJobMaterial, material]);

	React.useEffect(() => {
		if (!material) {
			setCostHistories(null);
		} else {
			loadCostHistories();
		}
	}, [loadCostHistories, material, showModal]);

	const costHistoriesForDisplay = React.useMemo(() => {
		if (!costHistories) {
			return [];
		}
		const now = new Date();
		return costHistories.map((_ch, _i) => {
			const { validFrom } = _ch;
			const validUntil = _i === 0 ? null : costHistories[_i - 1].validFrom;

			let isCurrent = false;
			if (!!timezone) {
				const from = TimeUtils.parseMomentTimezone(validFrom, timezone, TimeFormat.DB_DATE_ONLY).toDate();
				const until = validUntil ? TimeUtils.parseMomentTimezone(validUntil, timezone, TimeFormat.DB_DATE_ONLY).toDate() : null;
				isCurrent = now >= from && (!until || now < until);
			}

			return {
				..._ch,
				isCurrent,
				validUntil,
			};
		});
	}, [costHistories, timezone]);

	const renderTextCell = React.useCallback((cell: CellContext<(typeof costHistoriesForDisplay)[0], string>) => {
		return cell.getValue();
	}, []);

	const renderTotalCostCell = React.useCallback((cell: CellContext<(typeof costHistoriesForDisplay)[0], number>) => {
		return dollarFormatterUnitPrice(2, 2).format(cell.getValue());
	}, []);

	const renderBaseCostCell = React.useCallback((cell: CellContext<(typeof costHistoriesForDisplay)[0], number>) => {
		return dollarFormatterUnitPrice(4, 2).format(cell.getValue());
	}, []);

	const createMaterialPercentageCellRenderer = React.useCallback((
		field: keyof (typeof costHistoriesForDisplay)[0],
		backupField: keyof (typeof costHistoriesForDisplay)[0]
	) => {
		return (cell: CellContext<(typeof costHistoriesForDisplay)[0], Nullable<number>>) => {
			const { original } = cell.row;
			const value = original[field];
			const backupValue = original[backupField];
			const rateSource = value === null ? MaterialCostSource.JOB : MaterialCostSource.JOB_MATERIAL;
			return <span><MaterialRateSource rateSource={rateSource} /> {value ?? backupValue} %</span>;
		};
	}, []);

	const renderShippingMarkupCell = React.useMemo(() => createMaterialPercentageCellRenderer('shippingMarkup', 'jobShippingMarkup'), [createMaterialPercentageCellRenderer]);
	const renderSalesMarkupMarkupCell = React.useMemo(() => createMaterialPercentageCellRenderer('salesMarkup', 'jobSalesMarkup'), [createMaterialPercentageCellRenderer]);
	const renderTaxCell = React.useMemo(() => createMaterialPercentageCellRenderer('tax', 'jobTax'), [createMaterialPercentageCellRenderer]);

	const renderLongDateCell = React.useCallback((cell: CellContext<(typeof costHistoriesForDisplay)[0], Nullable<string>>) => {
		const value = cell.getValue();
		if (!value) {
			return 'N/A';
		}
		return TimeUtils.formatDate(cell.getValue(), TimeFormat.FULL_DATE_LONG, TimeFormat.ISO_DATETIME);
	}, []);

	const renderShortDateCell = React.useCallback((cell: CellContext<(typeof costHistoriesForDisplay)[0], string>) => {
		const value = cell.getValue();
		if (!value || value === PLATFORM_EARLIEST_DATE) {
			return '-';
		}
		return TimeUtils.formatDate(value, TimeFormat.DATE_ONLY, TimeFormat.DB_DATE_ONLY);
	}, []);

	const renderCurrentCell = React.useCallback((cell: CellContext<(typeof costHistoriesForDisplay)[0], boolean>) => {
		const value = cell.getValue();
		return !value ? '' : <b>Current</b>;
	}, []);

	const costHistoryColumns = React.useMemo<SimpleTableProps<(typeof costHistoriesForDisplay)[0]>['columns']>(() => [
		{
			id: 'baseCost',
			accessor: 'baseCost',
			header: 'Base Cost',
			cell: renderBaseCostCell,
			enableSorting: true,
		},
		{
			id: 'shippingMarkup',
			accessor: 'shippingMarkup',
			header: 'Shipping Markup (%)',
			cell: renderShippingMarkupCell,
			enableSorting: true,
		},
		{
			id: 'salesMarkup',
			accessor: 'salesMarkup',
			header: 'Sales Markup (%)',
			cell: renderSalesMarkupMarkupCell,
			enableSorting: true,
		},
		{
			id: 'tax',
			accessor: 'tax',
			header: 'Tax (%)',
			cell: renderTaxCell,
			enableSorting: true,
		},
		{
			id: 'totalCost',
			accessor: 'totalCost',
			header: 'Total Cost',
			cell: renderTotalCostCell,
			enableSorting: true,
		},
		{
			id: 'validFrom',
			accessor: 'validFrom',
			header: 'Valid From',
			cell: renderShortDateCell,
			enableSorting: true,
		},
		{
			id: 'validUntil',
			accessor: 'validUntil',
			header: 'Valid To',
			cell: renderShortDateCell,
			enableSorting: true,
		},
		{
			id: 'updatedBy',
			accessor: 'updatedBy',
			header: 'Change Made By',
			cell: renderTextCell,
			enableSorting: true,
		},
		{
			id: 'updatedAt',
			accessor: 'updatedAt',
			header: 'Change Made On',
			cell: renderLongDateCell,
		},
		{
			id: 'isCurrent',
			accessor: 'isCurrent',
			header: '',
			cell: renderCurrentCell,
		},
	], [
		renderTotalCostCell,
		renderBaseCostCell,
		renderCurrentCell,
		renderLongDateCell,
		renderSalesMarkupMarkupCell,
		renderShippingMarkupCell,
		renderShortDateCell,
		renderTaxCell,
		renderTextCell,
	]);

	const title = `Cost and Rate History for ${material?.displayName ?? ''}`;

	return (
		<CustomModal modalStyle="warning" showModal={showModal} size="xxl">
			<CustomModal.Header title={title} />
			<CustomModal.Body>
				<SimpleTable
					columns={costHistoryColumns}
					rows={costHistoriesForDisplay}
				/>
			</CustomModal.Body>
			<CustomModal.Footer>
				<Button
					label="Close"
					onClick={close}
					style="secondary"
				/>
			</CustomModal.Footer>
		</CustomModal>
	);
};

function mapStateToProps(state: RootState) {
	const { user } = state;

	const { companyData } = user;
	if (!companyData) {
		throw new Error('Not logged in');
	}

	const { timezone } = companyData;

	return { timezone };
}

function mapDispatchToProps() {
	return {
		findForJobMaterial: JobMaterialCostHistoryActions.findForJobMaterial,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

export default connector(RateHistoryModal);
